import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import api from "../../api";
import ShopProductCard from "../../components/Shop/ShopProductCard";
import CategoryFilters from "../../components/Shop/CategoryFilters";
import { useShopFilter } from "../../context/ShopFilterContext";
import RootCategoryPage from "../../components/Shop/RootCategoryPage";
import ParentCategoryPage from "../../components/Shop/ParentCategoryPage";

export default function ProductListPage() {
  const { category_slug } = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const urlCategorySlug = searchParams.get("category_slug") || category_slug;
  const urlCampaignSlug = searchParams.get("campaign_slug");
  const urlSearchTerm = searchParams.get("search");
  const supplierId = searchParams.get("supplier_id");


  const [products, setProducts] = useState([]);
  const [filtersData, setFiltersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  const {
    contextSearchTerm,
    setSearchTerm,
    appliedFilters,
    ordering,
    setAppliedFilters,
    setOrdering,
  } = useShopFilter();

  const DEFAULT_ORDERING = "-created_at";

  const SORT_OPTIONS = [
    { label: "جدیدترین", value: "-created_at" },
    { label: "قدیمی‌ترین", value: "created_at" },
    { label: "ارزان‌ترین", value: "min_price" },
    { label: "گران‌ترین", value: "-min_price" },
    { label: "پربازدیدترین", value: "-views_count" },
  ];

  // مدیریت بسته‌شدن دراپ‌داون با کلیک خارج از آن
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchProducts = useCallback(
    async (filters = {}, orderingValue = ordering || DEFAULT_ORDERING) => {
      const effectiveSearch = urlSearchTerm || "";

      setSearchTerm(effectiveSearch);

      if (!urlCategorySlug && !urlCampaignSlug && !supplierId && !effectiveSearch) {
        setProducts([]);
        setData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        if (urlCampaignSlug) params.append("campaign_slug", urlCampaignSlug);
        if (urlCategorySlug) params.append("category_slug", urlCategorySlug);
        if (effectiveSearch) params.append("search", effectiveSearch);
        if (supplierId) params.append("supplier_id", supplierId);
        if (orderingValue) params.append("ordering", orderingValue);

        Object.entries(filters).forEach(([key, value]) => {
          if (!value) return;

          if (Array.isArray(value)) {
            if (value.length > 0) params.append(key, value.join(","));
            return;
          }

          if (typeof value === "object") {
            if (value.min !== undefined && value.min !== null) {
              params.append(`${key}_min`, value.min);
            }
            if (value.max !== undefined && value.max !== null) {
              params.append(`${key}_max`, value.max);
            }
            return;
          }

          params.append(key, value);
        });

        const res = await api.get(`/products/shopping/?${params.toString()}`);

        let productList = [];
        if (Array.isArray(res.data.results)) productList = res.data.results;
        else if (Array.isArray(res.data.products)) productList = res.data.products;
        else if (Array.isArray(res.data)) productList = res.data;

        setProducts(productList);
        setData(res.data);
      } catch (err) {
        setError("خطا در دریافت محصولات");
      } finally {
        setLoading(false);
      }
    },
    [urlCategorySlug,urlCampaignSlug, supplierId, urlSearchTerm, ordering, setSearchTerm]
  );

  const fetchFilters = useCallback(async () => {
    setFiltersLoading(true);

    try {
      if (urlSearchTerm) {
        const res = await api.get("/products/search/filters/", {
          params: { search: urlSearchTerm },
        });
        setFiltersData(res.data);
        return;
      }
      if (supplierId) {
        const res = await api.get("/products/supplier/filters/", {
          params: { supplier_id: supplierId },
        });
        setFiltersData(res.data);
        return;
      }
      if (urlCampaignSlug) {
        const res = await api.get("/products/campaign/filters/", {
          params: { campaign_slug: urlCampaignSlug },
        });
        setFiltersData(res.data);
        return;
      }

      if (urlCategorySlug) {
        const res = await api.get(
          `/products/categories/${urlCategorySlug}/filters/`
        );
        setFiltersData(res.data);
        return;
      }

      setFiltersData(null);
    } catch (err) {
      setFiltersData(null);
    } finally {
      setFiltersLoading(false);
    }
  }, [urlCategorySlug, urlSearchTerm]);

  useEffect(() => {
    setAppliedFilters({});
    setOrdering(DEFAULT_ORDERING);
  }, [urlCategorySlug, urlSearchTerm, setAppliedFilters, setOrdering]);

  useEffect(() => {
    fetchProducts(appliedFilters, ordering);
  }, [fetchProducts, ordering, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (urlSearchTerm && urlSearchTerm !== contextSearchTerm) {
      setSearchTerm(urlSearchTerm);
    }
  }, [urlSearchTerm, contextSearchTerm, setSearchTerm]);

  useEffect(() => {
    if (!urlSearchTerm && contextSearchTerm) {
      setSearchTerm("");
    }
  }, [urlSearchTerm, contextSearchTerm, setSearchTerm]);

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    fetchProducts({}, ordering || DEFAULT_ORDERING);
  }, [setAppliedFilters, fetchProducts, ordering]);

  const handleApplyFilters = useCallback(() => {
    fetchProducts(appliedFilters, ordering || DEFAULT_ORDERING);
  }, [fetchProducts, appliedFilters, ordering]);

  const handleOrderingChange = useCallback(
    (value) => {
      setOrdering(value);
      setIsSortDropdownOpen(false);
      fetchProducts(appliedFilters, value);
    },
    [setOrdering, fetchProducts, appliedFilters]
  );

  const currentSortLabel =
    SORT_OPTIONS.find((opt) => opt.value === ordering)?.label || "جدیدترین";

  const shouldShowProductsArea =
    Boolean(urlSearchTerm) || Boolean(urlCampaignSlug) || Boolean(supplierId) || data?.view_type === "leaf" || Array.isArray(data);

  if (loading && !data && !products.length) {
    return <div className="py-20 text-center text-gray-500">در حال بارگذاری...</div>;
  }

  if (error) {
    return <div className="py-20 text-center text-red-600">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {data?.view_type === "root" && (
        <RootCategoryPage
          category={data.category}
          childrenCategories={data.children_categories}
        />
      )}

      {data?.view_type === "parent" && (
        <ParentCategoryPage
          category={data.category}
          subcategoriesWithProducts={data.subcategories_with_products}
        />
      )}

      {shouldShowProductsArea && (
        <div className="mb-4 border-b border-gray-200 pb-3">
          {/* دسکتاپ: تب‌های مرتب‌سازی افقی */}
          <div className="hidden items-center gap-3 md:flex">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-[#1E3A5F]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                />
              </svg>
              <span>مرتب‌سازی:</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleOrderingChange(option.value)}
                  className={`rounded-lg border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 lg:text-sm ${
                    ordering === option.value
                      ? "border-[#1E3A5F] bg-[#1E3A5F] text-[#FFC107] shadow-sm"
                      : "border-gray-200 bg-white text-gray-600 hover:border-[#1E3A5F] hover:bg-gray-50 hover:text-[#1E3A5F]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* موبایل: دکمه کشویی مرتب‌سازی */}
          <div className="relative md:hidden" ref={sortDropdownRef}>
            <button
              onClick={() => setIsSortDropdownOpen((prev) => !prev)}
              type="button"
              className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-xs transition-colors active:bg-gray-50"
            >
              <div className="flex items-center gap-2 text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-[#1E3A5F]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                  />
                </svg>
                <span className="font-medium text-gray-500">مرتب‌سازی بر اساس:</span>
                <span className="font-semibold text-[#1E3A5F]">
                  {currentSortLabel}
                </span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                  isSortDropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* لیست کشویی گزینه‌ها در موبایل */}
            {isSortDropdownOpen && (
              <div className="absolute right-0 z-30 mt-2 w-full origin-top-right rounded-xl border border-gray-100 bg-white py-1.5 shadow-lg ring-1 ring-black/5">
                {SORT_OPTIONS.map((option) => {
                  const isSelected = ordering === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleOrderingChange(option.value)}
                      type="button"
                      className={`flex w-full items-center justify-between px-4 py-2.5 text-right text-sm transition-colors ${
                        isSelected
                          ? "bg-blue-50/70 font-semibold text-[#1E3A5F]"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span>{option.label}</span>
                      {isSelected && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-[#1E3A5F]"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {shouldShowProductsArea && (
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <CategoryFilters
                filtersData={filtersData}
                onChange={setAppliedFilters}
                onClear={handleClearFilters}
                isLoading={filtersLoading}
                onApply={handleApplyFilters}
            />

            <main className="grid flex-1 grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
              {products.length > 0 ? (
                  products.map((product) => (
                      <Link
                          key={product.id}
                          to={`/shopping/product-detail/${product.slug}`}
                          className="block h-full min-w-0"
                      >
                        <ShopProductCard product={product}/>
                      </Link>
                  ))
              ) : (
                  <div className="col-span-full py-20 text-center text-gray-500">
                    محصولی یافت نشد
                  </div>
              )}
            </main>

          </div>
      )}
    </div>
  );
}
